<template>
  <div class="w-full">
    <div class="bg-white border min-h-[260px] border-gray-300 relative p-4">
      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex items-center gap-3 relative"
        :class="{ '!border-b-danger': error }"
      >
        <label
          for="event_name"
          class="font-normal w-32 text-gray-400 text-[14px] required"
          >Event Name
        </label>
        <input
          type="text"
          id="event_name"
          v-model="qrcreate.event.name"
          class="h-[22px] border-none shadow-none placeholder-gray-300 outline-none focus:outline-none w-full"
        />
      </div>
      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex gap-3 relative"
      >
        <label
          for="event_desc"
          class="font-normal w-32 text-gray-400 text-[14px]"
          >Description
        </label>
        <textarea
          type="text"
          id="event_desc"
          v-model="qrcreate.event.description"
          class="min-h-[22px] border-none shadow-none placeholder-gray-300 outline-none focus:outline-none w-full"
        />
      </div>
      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex items-center gap-3 relative"
      >
        <label
          for="event_place"
          class="font-normal w-32 text-gray-400 text-[14px]"
          >Place
        </label>
        <input
          type="text"
          id="event_place"
          v-model="qrcreate.event.place"
          class="h-[22px] border-none shadow-none placeholder-gray-300 outline-none focus:outline-none w-full"
        />
      </div>
      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex items-center gap-3 relative"
      >
        <label
          for="when_event"
          class="font-normal w-32 text-gray-400 text-[14px]"
          >When
        </label>
        <div
          class="flex items-center xs:gap-x-6 xs:gap-y-0 gap-y-1 xs:flex-row flex-col"
        >
          <input
            type="date"
            v-model="qrcreate.event.start"
            class="h-[22px] border-none shadow-none placeholder-gray-300 outline-none focus:outline-dashed focus:outline-gray-200 w-full"
            id="when_event"
          />
          <span>
            <ChevronsRightIcon stroke-width="1" />
          </span>
          <input
            v-model="qrcreate.event.end"
            type="date"
            class="h-[22px] border-none shadow-none placeholder-gray-300 outline-none focus:outline-dashed focus:outline-gray-200 w-full"
            id="when_event"
          />
        </div>
      </div>
      <div
        class="pt-3 pb-1.5 border-b border-b-gray-200 border-dashed flex items-center gap-3 relative"
      >
        <label for="encrypt" class="font-normal w-32 text-gray-400 text-[14px]"
          >Event Type
        </label>
        <Listbox v-model="qrcreate.event.type" id="encrypt">
          <div class="relative w-full">
            <ListboxButton
              class="relative w-full cursor-pointer h-[22px] text-left"
            >
              {{ qrcreate.event.type }}
            </ListboxButton>

            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                class="absolute z-10 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              >
                <ListboxOption
                  v-slot="{ active, selected }"
                  v-for="encrypt in events_type"
                  :value="encrypt"
                  as="template"
                >
                  <li
                    :class="[
                      active ? 'bg-success/10 text-success' : 'text-gray-900',
                      'relative cursor-default select-none py-2 pl-10 pr-4',
                    ]"
                  >
                    <span
                      :class="[
                        selected ? 'font-medium' : 'font-normal',
                        'block truncate',
                      ]"
                      >{{ encrypt }}</span
                    >
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3 text-success"
                    >
                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>
      </div>
    </div>
    <ToolsButtonCreate @generate="generate" />
  </div>
</template>
<script lang="ts" setup>
import { useQrCreateStore } from "~/stores/qrcreate";
import DataLocale from "~~/config/interface/DataLocale";
import { ChevronsRightIcon, CheckIcon } from "vue-tabler-icons";
import {
  Listbox,
  ListboxOption,
  ListboxOptions,
  ListboxButton,
} from "@headlessui/vue";
const error = ref(false);
const qrcreate = useQrCreateStore();
const qrstyle = useQrStyle();
const events_type = ref<Array<string>>(["Event Calendar", "Google Calendar"]);
const validateData = () => {
  if (qrcreate.event.name) {
    return true;
  }
  error.value = true;
  return false;
};
const generate = async () => {
  if (validateData()) {
    await qrstyle.GenerateQrCode({ stack: true });
  }
};

const dataLocales = new DataLocale([
  {
    locale: "en",
    name: "Event name",
    description: "Description",
    place: "Place",
    when: "When",
    type: "Event type",
  },
  {
    locale: "fr",
    name: "Nom de l'événement",
    description: "Description",
    place: "Lieu",
    when: "Quand",
    type: "Type d'événement",
  },
  {
    locale: "it",
    name: "Nome dell'evento",
    description: "Descrizione",
    place: "Luogo",
    when: "Quando",
    type: "Tipo di evento",
  },
  {
    locale: "es",
    name: "Nombre del evento",
    description: "Descripción",
    place: "Lugar",
    when: "Cuándo",
    type: "Tipo de evento",
  },
  {
    locale: "pt",
    name: "Nome do evento",
    description: "Descrição",
    place: "Local",
    when: "Quando",
    type: "Tipo de evento",
  },
  {
    locale: "nl",
    name: "Naam van het evenement",
    description: "Beschrijving",
    place: "Plaats",
    when: "Wanneer",
    type: "Soort evenement",
  },
  {
    locale: "zh",
    name: "活动名称",
    description: "描述",
    place: "地点",
    when: "时间",
    type: "活动类型",
  },
  {
    locale: "ja",
    name: "イベント名",
    description: "説明",
    place: "場所",
    when: "いつ",
    type: "イベントの種類",
  },
  {
    locale: "de",
    name: "Veranstaltungsname",
    description: "Beschreibung",
    place: "Ort",
    when: "Wann",
    type: "Veranstaltungstyp",
  },
  {
    locale: "vi",
    name: "Tên sự kiện",
    description: "Mô tả",
    place: "Địa điểm",
    when: "Khi nào",
    type: "Loại sự kiện",
  },
]);
</script>
